import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';

// API base URL
const BaseUrl = process.env.REACT_APP_BASH_URL;
const API_URL = `${BaseUrl}/admin/categories`;

// Async Thunks

// Create a new category
export const createCategory = createAsyncThunk(
    'categories/createCategory',
    async (categoryData, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem("admin-auth-token");
            const options = {
                Authorization: `Bearer ${token}`,
                "Content-Type": "application/json",
            };
            const response = await axios.post(`${API_URL}/create`, categoryData, { headers: options });
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response.data);
        }
    }
);

export const fetchProductCategories = createAsyncThunk(
    'categories/ProductCategories',
    async (_, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem("admin-auth-token");
            const options = {
                Authorization: `Bearer ${token}`,
                "Content-Type": "application/json",
            };
            const response = await axios.get(`${BaseUrl}/admin/product/categories`, { headers: options });
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response.data);
        }
    }
);

// Fetch all categories
export const fetchCategories = createAsyncThunk(
    'categories/fetchCategories',
    async (_, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem("admin-auth-token");
            const options = {
                Authorization: `Bearer ${token}`,
                "Content-Type": "application/json",
            };
            const response = await axios.get(`${API_URL}`, { headers: options });
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response.data);
        }
    }
);

// Fetch a single category by ID
export const fetchCategoryById = createAsyncThunk(
    'categories/fetchCategoryById',
    async (id, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem("admin-auth-token");
            const options = {
                Authorization: `Bearer ${token}`,
                "Content-Type": "application/json",
            };
            const response = await axios.get(`${API_URL}/${id}`, { headers: options });
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response.data);
        }
    }
);

// Update a category by ID
export const updateCategory = createAsyncThunk(
    'categories/updateCategory',
    async ({ id, formData }, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem("admin-auth-token");
            const options = {
                Authorization: `Bearer ${token}`,
                "Content-Type": "application/json",
            };
            const response = await axios.post(`${API_URL}/${id}`, formData, { headers: options });
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response.data);
        }
    }
);

// Delete a category by ID
export const deleteCategory = createAsyncThunk(
    'categories/deleteCategory',
    async (id, { dispatch,rejectWithValue }) => {
        try {
            const token = localStorage.getItem("admin-auth-token");
            const options = {
                Authorization: `Bearer ${token}`,
                "Content-Type": "application/json",
            };
            const response = await axios.delete(`${API_URL}/${id}`, { headers: options });
            dispatch(fetchCategories());
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response.data);
        }
    }
);

// Slice
const categoriesSlice = createSlice({
    name: 'categories',
    initialState: {
        categories: [],
        productCategories: [],
        selectedCategory: null,
        loading: false,
        error: null,
        sortBy: 'name',
        sortOrder: 'asc',
        currentPage: 1,
        itemsPerPage: 10,
    },
    reducers: {
        clearSelectedCategory: (state) => {
            state.selectedCategory = null;
        },
        setSortBy: (state, action) => {
            state.sortBy = action.payload;
        },
        setSortOrder: (state, action) => {
            state.sortOrder = action.payload;
        },
        setCurrentPage: (state, action) => {
            state.currentPage = action.payload;
        },
    },
    extraReducers: (builder) => {
        builder
            // Create Category
            .addCase(createCategory.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(createCategory.fulfilled, (state, action) => {
                state.loading = false;
                state.categories.push(action.payload);
            })
            .addCase(createCategory.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })
             .addCase(fetchProductCategories.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchProductCategories.fulfilled, (state, action) => {
                state.loading = false;
                state.productCategories = action.payload;
            })
            .addCase(fetchProductCategories.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })
            // Fetch all Categories
            .addCase(fetchCategories.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchCategories.fulfilled, (state, action) => {
                state.loading = false;
                state.categories = action.payload;
            })
            .addCase(fetchCategories.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })
            // Fetch Category by ID
            .addCase(fetchCategoryById.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchCategoryById.fulfilled, (state, action) => {
                state.loading = false;
                state.selectedCategory = action.payload;
            })
            .addCase(fetchCategoryById.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })
            // Update Category
            .addCase(updateCategory.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(updateCategory.fulfilled, (state, action) => {
                state.loading = false;
                const index = state.categories.findIndex(cat => cat._id === action.payload._id);
                if (index !== -1) {
                    state.categories[index] = action.payload;
                }
            })
            .addCase(updateCategory.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })
            // Delete Category
            .addCase(deleteCategory.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(deleteCategory.fulfilled, (state, action) => {
                state.loading = false;
                state.categories = state.categories.filter(cat => cat._id !== action.payload._id);
            })
            .addCase(deleteCategory.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            });
    },
});

// Export actions and reducer
export const { clearSelectedCategory, setSortBy, setSortOrder, setCurrentPage } = categoriesSlice.actions;
export default categoriesSlice.reducer;
